import { useEffect, useState } from "react";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { DropdownFilter } from "@components/common";
import {
  Button,
  SearchBox,
  useFilter,
  useSearch,
} from "@transfr-inc/dashboard-components/forms";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";
import { formatTimestap } from "@transfr-inc/dashboard-components/utils";

import { useApiRequest } from "@lib/hooks";
import container from "@src/container";

import { getSimsNames, searchSimsByName } from "./classroom.modules.utils";
import { ClassroomSimulationsFilter } from "./classroom.ts.databoxes";

import "./classroom.insights.scss";

export function ClassroomInsights({
  classroom,
  masteryStats,
  onStatusSelected,
  onModuleSelected,
  onSimNameSelected,
  disabled,
  data,
  filterSelected,
  searchTerm,
  currentModuleId,
}) {
  const [filters, setFilters] = useState([]);
  const [modules, setModules] = useState(data);
  const [simsNames, setSimsNames] = useState();

  const { classroomId, name } = classroom;
  const { insightsService } = container;

  const exportInsightsRequest = () => {
    const { clientCode: orgCode } = classroom;

    const filterDisplayName = filterSelected?.name;

    const fileName = filterDisplayName
      ? `${name} - ${filterDisplayName} - ${formatTimestap()}`
      : `${name} - ${formatTimestap()}`;

    return insightsService.exportClassroomData(
      classroomId,
      orgCode,
      fileName,
      currentModuleId
    );
  };

  const { loading: downloadingFile, sendRequest: exportInsights } =
    useApiRequest(exportInsightsRequest, false);

  useUnloadWarning({ trigger: downloadingFile });

  const filterByModule = useFilter({
    data: modules,
    filterKey: "displayName",
  });

  const simsNamesSearch = useSearch({
    data: modules,
    queryFn: searchSimsByName,
  });

  const onFilterStatusSelected = (optionSelected) => {
    const results = filterByModule.queryData(optionSelected?.name);
    onModuleSelected?.(results);
  };

  const onSimsNameTermChange = (term) => {
    !term && onSimNameSelected?.();
  };

  const onSimsNameSelected = (term) => {
    const results = simsNamesSearch.queryData(term);
    const simulation = results[0]?.simulations[0] || {};
    onSimNameSelected?.(simulation);
  };

  const updateFilterOptions = (data) => {
    const rawOptions = data.map((d) => ({
      id: d.moduleId,
      name: d.displayName,
    }));

    const uniqueOptions = Array.from(
      new Map(rawOptions.map((item) => [item.id, item])).values()
    );

    setFilters(uniqueOptions);
    filterByModule.updateData(data);
  };

  useEffect(() => {
    if (data) {
      setModules(data);
      setSimsNames(getSimsNames(data));
      simsNamesSearch.updateData(data);
      updateFilterOptions(data);
    }
  }, [data]);

  return (
    <div className="classroom-insights-section">
      <div className="header">
        <div className="right-section">
          <h2>Simulations</h2>
        </div>
        <div className="left-section">
          <SearchBox
            className="sims-search"
            placeholder="Search"
            data={simsNames}
            disabled={!modules}
            onSearchTermChange={onSimsNameTermChange}
            onResultSelected={onSimsNameSelected}
            searchTerm={searchTerm}
          ></SearchBox>

          <DropdownFilter
            className="module-filter"
            data={filters}
            value={filterSelected}
            onOptionSelected={onFilterStatusSelected}
            placeholder="All Modules"
          ></DropdownFilter>

          <Button
            primary
            size="small"
            icon={["fa-light", "file-export"]}
            onClick={exportInsights}
            disabled={downloadingFile || disabled}
          >
            Export Data (.csv)
          </Button>
        </div>
      </div>
      <div className="insights-databoxes">
        <ClassroomSimulationsFilter
          masteryStats={masteryStats}
          onStatusSelected={onStatusSelected}
          disabled={disabled}
        ></ClassroomSimulationsFilter>
      </div>
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of <strong>{`${name}'s`}</strong>{" "}
            insights. Please wait...
          </Notification>
        )}
      </div>
    </div>
  );
}
